import React from "react";
import {
  Link,
  ListItem,
  OrderedList,
  Text,
  UnorderedList,
  VStack,
} from "@chakra-ui/layout";
import { FAQProps } from "../../_components/FAQs/FAQs";
import { Link as RouterLink } from "react-router-dom";

export const businessFAQs: FAQProps["items"] = [
  {
    title: "What is LaborHack?",
    body: "LaborHack is a platform that connects construction companies, facility management companies, SMEs, and homeowners with skilled artisans to manage their projects, facilities, or homes.",
  },
  {
    title: "What is LaborHack Recruit?",
    body: "LaborHack Recruit is a hiring platform that helps employers connect with vetted artisans and skilled workers. Employers can post jobs to their private community or the broader LaborHack network and manage the entire recruitment process from one dashboard.",
  },
  {
    title: "How does the job posting process work?",
    body: (
      <VStack alignItems="start" spacing={2}>
        <Text>Once you sign up, you can post a job advert to:</Text>
        <UnorderedList>
          <ListItem>
            Your private job board (restricted to your own community of
            artisans).
          </ListItem>
          <ListItem>
            The public LaborHack job board (visible to the broader LaborHack
            community).
          </ListItem>
        </UnorderedList>
      </VStack>
    ),
  },
  {
    title: "How do workers get notified about new jobs?",
    body: "When you post a job, notifications are automatically sent to artisans whose profiles match the skills, experience, and tags you selected.",
  },
  {
    title: "How do I manage applications?",
    body: (
      <VStack alignItems="start" spacing={2}>
        <Text>Employers can review applicants in their dashboard and:</Text>
        <UnorderedList>
          <ListItem>Reject candidates</ListItem>
          <ListItem>Shortlist candidates</ListItem>
          <ListItem>Hire directly</ListItem>
        </UnorderedList>
      </VStack>
    ),
  },
  {
    title: "Can I review candidates’ profiles before hiring?",
    body: "Yes. Each applicant’s LaborHack portfolio includes their verified details, certifications, and past job history for you to evaluate before making a decision.",
  },
  {
    title: "Is there any automation to help with shortlisting?",
    body: "Yes. LaborHack includes an Applicant Tracking System (ATS) that ranks and filters candidates based on your criteria, making the review process faster.",
  },
  {
    title: "Do I pay to post jobs?",
    body: "No. You pay for seats for your organization, not per job post. This gives your team ongoing access to post and manage multiple roles.",
  },
  {
    title: "Can I communicate directly with candidates?",
    body: "Currently, communication happens outside the platform. You can reach out to candidates via the contact information in their portfolios after shortlisting.",
  },
  {
    title: "What happens if I want to fill recurring roles?",
    body: "Employers can re-post jobs or keep positions open for ongoing applications, and the system will keep notifying matching artisans.",
  },
];

export const communityFAQs: FAQProps["items"] = [
  {
    title: "What is LaborHack?",
    body: "LaborHack is a platform that connects construction companies, facility management companies, SMEs, and homeowners with skilled artisans to manage their projects, facilities, or homes.",
  },
  {
    title: "What is the Community Manager Tool?",
    body: "The Community Manager Tool helps organizations, schools, alumni networks, and associations manage and engage their members in one central dashboard.",
  },
  {
    title: "How does the onboarding process work?",
    body: "Once an organization signs up, they can upload their community list directly into the dashboard.",
  },
  {
    title: "What happens after we upload our community?",
    body: (
      <VStack alignItems="start" spacing={2}>
        <UnorderedList>
          <ListItem>
            Existing LaborHack users get notified that they’ve been added to
            your organization.
          </ListItem>
          <ListItem>
            New users receive an invitation to join your organization, prompting
            them to sign up and complete their LaborHack portfolio.
          </ListItem>
        </UnorderedList>
      </VStack>
    ),
  },
  {
    title: "Do members need to pay to join my organization on LaborHack?",
    body: "No. Community members can join for free once invited.",
  },
  {
    title: "How are members notified?",
    body: "Invitations and notifications are sent automatically through email.",
  },
  {
    title: "What can I do after members join my community?",
    body: "You can post jobs or projects to your community, track engagement, and connect your members with wider LaborHack opportunities.",
  },
  {
    title: "Do I need technical skills to set this up?",
    body: "No. The process is straightforward — upload your community, and the system handles the notifications and onboarding automatically.",
  },
];

export const homeownerFAQs: FAQProps["items"] = [
  {
    title: "What is LaborHack for Homeowners?",
    body: "It's a service that allows homeowners to book trusted artisans for repairs, maintenance, and improvement projects.",
  },
  {
    title: "How do I know the artisans are reliable?",
    body: "Our artisans are pre-vetted through ID checks, background verification, and skills testing.",
  },
  {
    title: "What kind of services can I request?",
    body: "Plumbing, electrical work, carpentry, painting, cleaning, appliance repair, and more.",
  },
  {
    title: "How do I make a booking?",
    body: "Simply log into your homeowner dashboard, select the service you need, and schedule a visit.",
  },
  {
    title: "What happens if the job is not done to my satisfaction?",
    body: "LaborHack provides quality assurance — if you’re not satisfied, we’ll resolve the issue or send a replacement artisan.",
  },
  {
    title: "Is payment made upfront?",
    body: "Yes, payments are secured through the platform to protect both homeowners and artisans.",
  },
];

export const skilledWorkersFAQs: FAQProps["items"] = [
  {
    title: "What is LaborHack?",
    body: "LaborHack is a platform that connects construction companies, facility management companies, SMEs, and homeowners with skilled workers to manage their projects, facilities, or homes.",
  },
  {
    title: "Who can use LaborHack?",
    body: "LaborHack is designed to assist artisans, skilled workers, and professionals earn more and access tools for growth",
  },
  {
    title: "Is there a fee to join LaborHack?",
    body: "No, signing up on LaborHack is completely free. However, additional features like certifications or tool financing may have associated costs.",
  },
  {
    title: "How do I get started?",
    body: "Simply sign up for a LaborHack account, and create your profile",
  },
  {
    title: "Do I need to be certified to use LaborHack?",
    body: "No, certification is optional, but it helps boost your credibility and secure better opportunities.",
  },
];
